import {
  base_colorAction,
  base_colorActionHover,
  base_colorSurfaceSecondary,
  base_contentHover,
  cta_buttonPrimaryText,
  /* No semantic token carries this value in a text role — the only two that
     resolve to it are a tag background and an input border. */
  // eslint-disable-next-line no-restricted-imports
  grey500,
  input_borderDefault,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_family_weight_medium, font_md } from 'ext-ui/style/typography'
import { css } from 'styled-components'

/* react-dates' own captionPaddingTop, and the line box font_md opens. The nav
   arrows are absolutely positioned over the caption, so they need both to land
   on its line rather than at a guessed offset. Giving the caption selects the
   same line box keeps that one offset right whether the caption is text or
   controls — and keeps the calendar's measured title height identical either
   way, since react-dates measures it from an off-screen month. */
const CAPTION_PADDING_TOP = '22px'
const CAPTION_LINE_HEIGHT = '18px'

export default css`
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  &.calendar-popper {
    /* The popper portals to <body>, so it competes in the root stacking
       context with BaseModal/BaseFlyout overlays (z-index 10) that the field
       may sit inside. Must stay above them, and below HoverTooltip (1001). */
    z-index: 20;
  }

  .DayPicker {
    border-radius: ${spacing(2)};
    border: 1px solid ${base_colorAction};
    overflow: hidden;
  }

  .CalendarMonth_caption {
    font-family: ${font_family_weight_medium};
    color: ${base_colorAction};
    ${font_md};
    padding-bottom: ${spacing(12)};
  }

  .caption-selects {
    display: flex;
    align-items: center;
    justify-content: center;
    /* None of its own: each control already pads itself so the hover tint has
       room, which leaves the two reading as one phrase. */
    gap: 0;

    select {
      /* Caption text that happens to be pickable: the UA chrome, and with it
         the dropdown arrow, is stripped in all three engines. Still a native
         <select>, so keyboard and screen-reader behaviour comes for free —
         which is also why the aria-labels are not optional here, the control
         no longer looks like one. */
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      font: inherit;
      line-height: inherit;
      height: ${CAPTION_LINE_HEIGHT};
      box-sizing: border-box;
      color: ${base_colorAction};
      background: none;
      border: 0;
      border-radius: ${spacing(1)};
      margin: 0;
      padding: 0 ${spacing(1)};
      cursor: pointer;

      &:hover {
        /* The token the Dropdown's own options hover with. */
        background-color: ${base_contentHover};
        color: ${base_colorActionHover};
      }

      &:focus-visible {
        outline: 2px solid ${base_colorAction};
        outline-offset: 2px;
      }
    }

    .caption-month {
      /* Wide enough for the longest month name, so picking a shorter one
         cannot reflow the caption or move the nav arrows under the cursor. */
      min-width: ${spacing(20)};
      text-align: center;
    }
  }

  .CalendarDay {
    border: 1px solid ${input_borderDefault};
    background-color: ${base_colorSurfaceSecondary};
  }

  /* Inset so the ring stays inside the 1px cell border rather than bleeding
     over the neighbouring days. */
  .CalendarDay:focus-visible {
    outline: 2px solid ${base_colorActionHover};
    outline-offset: -2px;
  }

  .CalendarDay__selected,
  .CalendarDay:hover {
    background-color: ${base_colorActionHover};
    color: ${cta_buttonPrimaryText};
  }

  .CalendarDay__blocked_out_of_range {
    color: ${grey500};

    &:hover {
      background-color: ${base_colorSurfaceSecondary};
      color: ${grey500};
    }
  }

  .DayPickerKeyboardShortcuts_buttonReset {
    display: none;
  }

  .DayPickerNavigation {
    position: absolute;
    top: ${CAPTION_PADDING_TOP};
    left: 0;
    right: 0;
    height: ${CAPTION_LINE_HEIGHT};
    padding: 0 ${spacing(6)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* The bar is full-width and overlays the caption row, so without this it
       swallows every click aimed at the caption between the two arrows. */
    pointer-events: none;
  }

  &.has-caption-selects .DayPickerNavigation {
    /* Push the arrows out to the calendar's edges so they flank the selects
       rather than crowding them. */
    padding: 0 ${spacing(3)};
  }

  /* Bare chevrons: the buttons are ours (see renderNavButton), so react-dates'
     own bordered-box chrome never applies. */
  .calendar-nav {
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    border-radius: ${spacing(1)};

    &[aria-disabled='true'] {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:focus-visible {
      outline: 2px solid ${base_colorAction};
      outline-offset: 2px;
    }
  }

  .calendar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    border: none;
    padding: 0;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &:focus-visible {
      outline: 2px solid ${base_colorAction};
      outline-offset: 2px;
      border-radius: ${spacing(1)};
    }
  }
`
